import {observable, computed} from "mobx";
import {dispatchable, ActionStore} from "higher-order/ActionStore";
import clientErrorLogger from "util/clientErrorLogger";
import ModalModel from "gradifui/models/ModalModel";
import SessionModel from "./SessionModel";
import ApiGetter from "../ApiGetter";
import {URLS} from "util/constants";

class SessionExpiringModalModel extends ModalModel {
    @observable remainingTime = 0;
    @observable isOpen = false;

    constructor(isOpen, remainingTime) {
        super();
        this.isOpen = isOpen;
        this.remainingTime = remainingTime;
    }
}

export default class SessionStore extends ActionStore {
    @observable session;
    @observable expiringModal;
    @observable featureToggles;
    @observable externalUrls;
    @observable environment;

    @computed
    get employerFeatures() {
        return this.session?.features?.employer ?? {};
    }

    @computed
    get memberFeatures() {
        return this.session?.features?.member ?? {};
    }

    @computed
    get unreadNotificationCount() {
        return this.session?.unreadNotificationCount ?? 0;
    }

    @computed
    get notificationCount() {
        return this.session?.notificationCount ?? 0;
    }

    constructor(props, dispatcher) {
        super(dispatcher);
        this.apiGetter = new ApiGetter(dispatcher, {disableLoadSpinner: true});
        this.session = new SessionModel(props.session, dispatcher, this.apiGetter);
        this.expiringModal = new SessionExpiringModalModel(false, 0);
        this.featureToggles = props.session?.featureToggles ?? {};
        this.externalUrls = props.session?.externalUrls ?? {};
        this.environment = props.environment ?? "LOCAL";

        // update Sentry/Raven with session id and user id
        if (props.session) {
            if (props.session.id) {
                clientErrorLogger.setSessionId(props.session.id);
            }
            if (props.session?.user?.altId) {
                clientErrorLogger.setUserId(props.session.user.altId);
            }
        }
    }

    @dispatchable
    logout(e) {        
        let logoutUrl = `/logout${(e.data.redirectUrl ? "?redirectUrl=" + e.data.redirectUrl : "")}`;
        window.location.assign(logoutUrl);
    }

    @dispatchable
    sessionExtend(e) {
        this.session?.extendSession();
        this.dispatcher.emit("HIDE_SESSION_EXPIRING_MODAL");
        this.dispatcher.emit("LOAD_SESSION_DATA", {forceRefresh: true});
    }

    @dispatchable
    sessionExpiring(e) {
        this.expiringModal.remainingTime = e.data.remainingTime;
        this.expiringModal.open();
    }

    @dispatchable
    sessionEnd(e) {
        this.dispatcher.emit("LOGOUT");
        this.session?.broadcastLogout();
    }

    @dispatchable
    sessionExpired(e) {
        this.dispatcher.emit("DISABLE_NAVIGATION_WARNING_MODAL");
        this.dispatcher.emit("LOGOUT", {redirectUrl: this.session?.loginPath ?? "" + "?errorCode=3"});
        this.session?.broadcastLogout();
    }

    @dispatchable
    hideSessionExpiringModal(e) {
        this.expiringModal.close();
    }

    @dispatchable
    loadSessionData(e) {
        this.session?.loadFromServer(e.data.forceRefresh);
    }

    @dispatchable
    hideCtaBanner(e) {
        this.dispatcher.emit("SHOW_LOAD_SPINNER");
        this.apiGetter.post(URLS.API.HIDE_CTA_BANNER,
            (res) => {
                this.session?.loadFromServer(true);
                this.dispatcher.emit("HIDE_LOAD_SPINNER");
            },
            (err, res) => {
                this.dispatcher.emit("HIDE_LOAD_SPINNER");
                this.dispatcher.emit("SHOW_FLASH_MESSAGE", {
                    message: "Oh no! We ran into a problem processing your request. Please contact Customer Service if this problem persists.",
                    className: "warning"
                });
            });
    }
}
